import React, { useEffect, useState } from 'react';
import { CitizenCaseLookup } from '../../../api-service/clients/auto-generated-clients';
import { moduleClientFactory } from '../../../api-service/module-client-factory';
import { SingleSelect } from '../../single-select/single-select';
import { ReadonlyInput } from '../../readonly-input/readonly-input';
import { ICitizenCaseLookupProps } from './interfaces';
import { formatCaseLabel } from './formatters';

const lookupCaseClient = moduleClientFactory.createLookupCaseClient();

export function CitizenCaseLookup({
  caseId,
  citizenId,
  onChange,
  readOnly,
  required,
  error,
  errorText,
  autoSelectPrimaryCase,
  label = 'Sag',
  variant,
  showStatus = false,
}: ICitizenCaseLookupProps) {
  const [cases, setCases] = useState<CitizenCaseLookup[]>([]);
  const [selectedCase, setSelectedCase] = useState<CitizenCaseLookup | undefined>();

  useEffect(() => {
    if (!citizenId) {
      setCases([]);
      setSelectedCase(undefined);
      return;
    }

    const requestedCitizenId = citizenId;

    async function fetchCases() {
      const result = await lookupCaseClient.getCitizenCasesLookup({ citizenId: requestedCitizenId });
      const fetchedCases = result.citizenCases ?? [];
      setCases(fetchedCases);

      if (caseId) {
        setSelectedCase(fetchedCases.find(x => x.caseId === caseId));
      } else if (autoSelectPrimaryCase) {
        const primaryCase = fetchedCases.find(x => x.isPrimaryCase);
        setSelectedCase(primaryCase);
        onChange(primaryCase?.caseId, primaryCase);
      }
    }

    fetchCases();
  }, [caseId, citizenId]);

  function handleOnChange(value: CitizenCaseLookup | undefined) {
    onChange(value?.caseId, value);
    setSelectedCase(value);
  }

  return readOnly ? (
    <ReadonlyInput label={label} value={formatCaseLabel(selectedCase?.caseName, selectedCase?.caseStatus, showStatus)} />
  ) : (
    <SingleSelect<CitizenCaseLookup>
      label={label}
      placeholder="Vælg en af borgerens sager"
      isOptionEqualToValue={(option, val) => option?.caseId === val?.caseId}
      getOptionLabel={option => formatCaseLabel(option?.caseName, option?.caseStatus, showStatus)}
      renderOption={(optionProps, option) => (
        <li {...optionProps} key={option?.caseId}>
          <div>{formatCaseLabel(option?.caseName, option?.caseStatus, showStatus)}</div>
        </li>
      )}
      onChange={handleOnChange}
      options={cases}
      value={selectedCase}
      required={required}
      error={error}
      errorText={errorText}
      variant={variant}
    />
  );
}
